import React, { useEffect, useMemo } from 'react';
import {
  APP_TYPE,
  Invoice,
  getAppState,
  getSearchKey,
  getSelectedItemIds,
  getSendType,
  useAppSelector,
} from '@zonofi/zono-money-store';
import { useInvoiceList } from '../hooks';
import './invoice-list-screen-content.scss';
import {
  ChipFilters,
  CommonButton,
  EmptyStateView,
  EndListView,
  ListSkeleton,
  PullRefreshContainer,
  SpinnerLoader,
  searchComponents,
} from '../../components';
import { SVGS } from '../../assets';
import { InvoiceCard } from '../invoice-card';
import { isEmpty } from 'lodash';
import {
  FIELD_OPS_ROUTES,
  InvoiceStatusEnum,
  SELLER_WEBAPP_ROUTES,
  SearchConfig,
} from '../../constants';
import { useNavigate } from 'react-router-dom';
import { ChipFilterSection } from '../../types';
import { getTextFromRange } from '../../helper';

interface InvoiceListScreenContentProps {
  setInvoiceData?: React.Dispatch<React.SetStateAction<Invoice[]>>;
  selectInvoice?: boolean;
  setShowSearch?: (val: boolean) => void;
  searchConfig?: SearchConfig;
  showSearch?: boolean;
  onDateChipPress?: () => void;
  isCustomer?: boolean;
}

export const InvoiceListScreenContent: React.FC<
  InvoiceListScreenContentProps
> = ({
  setInvoiceData = () => {},
  selectInvoice = false,
  searchConfig = {} as SearchConfig,
  setShowSearch = () => {},
  showSearch = false,
  onDateChipPress = () => {},
  isCustomer = false,
}) => {
  const navigate = useNavigate();

  const {
    isLoading,
    invoiceData,
    onInvoiceItemPress,
    lastItemRef,
    onSearchCancel,
    isEndOfList,
    onScroll,
    statusFilters,
    handleShareInvoice,
    appliedDateRange,
    setAppliedStatus,
    refreshInvoiceList,
  } = useInvoiceList({ selectInvoice, isCustomer });

  const appState = useAppSelector(getAppState);
  const searchKey = useAppSelector(getSearchKey);
  const sendType = useAppSelector(getSendType);
  const selectedInvoiceIds = useAppSelector(getSelectedItemIds);

  const onContinuePress = () => {
    const routeToNavigate =
      appState === APP_TYPE.SELLER_HUB
        ? SELLER_WEBAPP_ROUTES
        : FIELD_OPS_ROUTES;
    navigate(routeToNavigate.VIEW_SELECTED_INVOICES);
  };

  useEffect(() => {
    setInvoiceData(invoiceData ?? []);
  }, [invoiceData]);

  const searchProp =
    appState === APP_TYPE.FIELDS_OPS
      ? { search: searchComponents({ searchConfig, setShowSearch }) }
      : undefined;

  const chips: ChipFilterSection[] = useMemo(
    () => [
      {
        items: [
          {
            id: '',
            value: getTextFromRange(appliedDateRange),
            selected: true,
            type: 'date',
          },
        ],
        onItemPress: onDateChipPress,
      },
      {
        items: statusFilters,
        onItemPress: ({ id }: { id: string }) =>
          setAppliedStatus(id ? [id as InvoiceStatusEnum] : []),
      },
    ],
    [appliedDateRange, statusFilters]
  );

  return (
    <>
      <div className={`invoice-list-content`}>
        {chips && (
          <ChipFilters
            sections={chips}
            chipFilterStyles={{
              marginTop: '-12px',
              alignSelf: 'flex-start',
              width: '100%',
              position: 'sticky',
              top: '-16px',
              left: '-16px',
              ...(appState === APP_TYPE.FIELDS_OPS &&
                searchConfig?.enabled && { padding: 0 }),
            }}
            {...(searchProp && searchProp)}
            showSearch={showSearch}
          />
        )}
        <PullRefreshContainer
          className="invoice-list-content-container"
          onScroll={onScroll}
          onRefresh={refreshInvoiceList}
        >
          {searchConfig.enabled && searchKey.length > 0 && (
            <div className="invoice-search-info font-regular">
              <span className="">
                RESULTS FOR{' '}
                <span className="font-semibold">{`"${searchKey}"`}</span>
              </span>
              <span
                className="clear-btn font-semibold"
                onClick={onSearchCancel}
              >
                CLEAR
              </span>
            </div>
          )}
          {isLoading ? (
            <ListSkeleton className="invoice-list-screen-skeleton" />
          ) : isEmpty(invoiceData) ? (
            <EmptyStateView
              imageSrc={SVGS.NoInvoicesFound}
              message={'No Invoices Found'}
            />
          ) : (
            <div className="invoice-list-view">
              {invoiceData?.map((invoiceItem) => (
                <InvoiceCard
                  key={invoiceItem.id}
                  invoiceItem={invoiceItem}
                  onInvoiceItemPress={onInvoiceItemPress}
                  showSelection={sendType?.length !== 0}
                  onShareInvoice={(invoiceItem: Invoice) =>
                    handleShareInvoice(invoiceItem)
                  }
                />
              ))}
              <div ref={lastItemRef} className="invoice-last-item" />
              {!isEndOfList && <SpinnerLoader />}
              {isEndOfList && <EndListView />}
            </div>
          )}
        </PullRefreshContainer>
      </div>

      {selectedInvoiceIds?.length > 0 && (
        <div className="continue-bottom-button">
          <CommonButton
            text="View Selected Invoices"
            onPress={onContinuePress}
          />
        </div>
      )}
    </>
  );
};
